
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, X } from "lucide-react";
import { StepProgress } from "@/components/ui/step-progress";
import { ProgramCreationProvider, useProgramCreation } from "@/contexts/ProgramCreationContext";
import ProgramCreationHeader from "@/components/programs/ProgramCreationHeader";
import ProgramCreationStepContent from "@/components/programs/ProgramCreationStepContent";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

// Define the steps for the program creation process
const PROGRAM_CREATION_STEPS = [
  { number: 1, title: "Client", description: "Select & review client" },
  { number: 2, title: "Program", description: "Program details" },
  { number: 3, title: "Questions", description: "Tier-specific info" },
  { number: 4, title: "Preferences", description: "Customizations" },
  { number: 5, title: "Review", description: "Final check" },
];

function ProgramCreationContent() {
  const { step, selectedClient, resetSession } = useProgramCreation();
  const { checkCanAddProgramToClient, isCheckingPermissions, isFounder } = usePermissionCheck();
  const [canAddProgram, setCanAddProgram] = React.useState(true);
  const navigate = useNavigate();

  // Check permissions when client is selected
  useEffect(() => {
    if (selectedClient && !isFounder) {
      const checkPermission = async () => {
        const canAdd = await checkCanAddProgramToClient(selectedClient.id);
        setCanAddProgram(canAdd);
      };
      checkPermission();
    } else if (isFounder) {
      // Founders can always add programs
      setCanAddProgram(true);
    }
  }, [selectedClient, checkCanAddProgramToClient, isFounder]);
  
  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel? All unsaved progress will be lost.")) {
      resetSession();
      navigate('/programs');
    }
  };
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <ProgramCreationHeader />
        <Button 
          variant="outline" 
          className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          onClick={handleCancel}
        >
          <X className="mr-2 h-4 w-4" /> Cancel
        </Button>
      </div>

      {/* Step Progress Component */}
      <StepProgress 
        steps={PROGRAM_CREATION_STEPS} 
        currentStep={step} 
        className="mb-8 animate-fade-in" 
      />

      {selectedClient && !canAddProgram ? (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Program Limit Reached</AlertTitle>
          <AlertDescription>
            This client has reached the maximum number of programs allowed for your current tier.
            Beta tier clients can have a maximum of 2 programs.
          </AlertDescription>
        </Alert>
      ) : (
        <ProgramCreationStepContent />
      )}
    </div>
  );
}

export default function ProgramCreationManager() {
  const navigate = useNavigate();

  return (
    <ProgramCreationProvider>
      <ProgramCreationContent />
    </ProgramCreationProvider>
  );
}
